import React, { useState } from "react";
import api from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Loader2, RefreshCw, AlertTriangle, CheckCircle2, Undo2, X } from "lucide-react";

/**
 * Two-stage re-enrichment UI:
 *
 *   [ Preview changes ]
 *      ↓
 *   Preview dialog opens with:
 *     - transition summary chips (no→yes: 312, yes→no: 2 ⚠ etc.)
 *     - per-transition sample rows
 *     - [ Cancel ]  [ Apply changes ]
 *
 *   After apply:
 *     - "Applied" state with row count + [ Undo last apply ] button
 *       (calls the rollback endpoint).
 *
 * Motivation: the re-enrichment path had 3 different data-corruption
 * bugs on 2026-07-09. Ops teams should be able to *see the exact diff*
 * before touching production. See `backend/app/reenrich_preview.py`.
 */

const TRANSITION_META = {
  "no→yes":               { label: "no → yes (gained credit)",       tone: "emerald" },
  "no→sms":               { label: "no → sms (gained SMS credit)",   tone: "emerald" },
  "no→unsure":            { label: "no → unsure",                    tone: "amber"   },
  "yes→no":               { label: "yes → no (LOST credit)",         tone: "red"     },
  "sms→no":               { label: "sms → no (LOST SMS credit)",     tone: "red"     },
  "unsure→no":            { label: "unsure → no",                    tone: "slate"   },
  "yes→sms":              { label: "yes → sms (downgraded)",         tone: "amber"   },
  "sms→yes":              { label: "sms → yes (upgraded)",           tone: "emerald" },
  "credit_date_changed":  { label: "credit-date shifted",            tone: "sky"     },
  "held_for_review":      { label: "held for manual review",         tone: "amber"   },
};

const TONE_CLASSES = {
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  red:     "bg-red-50 text-red-700 border-red-200",
  amber:   "bg-amber-50 text-amber-700 border-amber-200",
  sky:     "bg-sky-50 text-sky-700 border-sky-200",
  slate:   "bg-slate-50 text-slate-700 border-slate-200",
};

export function ReenrichPreviewButton({ brandId, dateFrom, dateTo, onApplied }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [applyState, setApplyState] = useState({ status: "idle", rollback_id: null, applied: 0 });

  const runPreview = async () => {
    if (!dateFrom || !dateTo) {
      setError("Pick a date range first.");
      setOpen(true);
      return;
    }
    setOpen(true);
    setLoading(true);
    setError(null);
    setPreview(null);
    setApplyState({ status: "idle", rollback_id: null, applied: 0 });
    try {
      const { data } = await api.post("/admin/reenrich-preview", {
        brand_id: brandId ? Number(brandId) : null,
        date_from: dateFrom, date_to: dateTo,
      });
      setPreview(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  const runApply = async (includeCreditStrips = false) => {
    if (!preview?.preview_id) return;
    setApplyState({ status: "applying", rollback_id: null, applied: 0 });
    try {
      const { data } = await api.post(
        `/admin/reenrich-apply/${preview.preview_id}`,
        null,
        { params: { include_credit_strips: includeCreditStrips } },
      );
      setApplyState({
        status: "applied",
        rollback_id: data.rollback_id,
        applied: data.applied,
        skipped_held_for_review: data.skipped_held_for_review || 0,
      });
      onApplied?.(data);
    } catch (e) {
      setApplyState({ status: "idle", rollback_id: null, applied: 0 });
      setError(e?.response?.data?.detail || e.message);
    }
  };

  const runRollback = async () => {
    if (!applyState.rollback_id) return;
    if (!window.confirm("Undo this apply? All affected rows will be restored to their pre-apply state.")) return;
    setApplyState((s) => ({ ...s, status: "rolling_back" }));
    try {
      await api.post(`/admin/reenrich-rollback/${applyState.rollback_id}`);
      setApplyState({ status: "rolled_back", rollback_id: null, applied: 0 });
      onApplied?.({ rolled_back: true });
    } catch (e) {
      setApplyState((s) => ({ ...s, status: "applied" }));
      setError(e?.response?.data?.detail || e.message);
    }
  };

  const summaryChips = () => {
    if (!preview?.transitions) return null;
    const kinds = Object.entries(preview.transitions)
      .filter(([, count]) => count > 0 && !["unchanged"].includes(_norm_k(String(count))))
      .filter(([k, count]) => count > 0 && k !== "unchanged");
    if (kinds.length === 0) {
      return (
        <div className="text-sm text-slate-500 py-3">
          No changes proposed — everything is already in sync with MySQL.
        </div>
      );
    }
    return (
      <div className="flex flex-wrap gap-2">
        {kinds.map(([k, count]) => {
          const meta = TRANSITION_META[k] || { label: k, tone: "slate" };
          return (
            <span key={k}
              className={`text-xs px-2 py-1 rounded-full border ${TONE_CLASSES[meta.tone]}`}
              data-testid={`reenrich-preview-chip-${k}`}
            >
              {meta.label}: <span className="font-semibold">{count.toLocaleString()}</span>
            </span>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <Button
        onClick={runPreview}
        disabled={loading || !dateFrom || !dateTo}
        data-testid="reenrich-preview-btn"
        className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
      >
        {loading
          ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Computing preview…</>
          : <><RefreshCw className="w-4 h-4 mr-2" /> Preview re-enrichment</>}
      </Button>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setError(null); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="reenrich-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-4 h-4 text-fuchsia-600" />
              Preview re-enrichment
              <span className="text-xs font-normal text-slate-500 ml-2">
                {dateFrom} → {dateTo} · {brandId ? `brand ${brandId}` : "all brands"}
              </span>
            </DialogTitle>
          </DialogHeader>

          {loading && (
            <div className="py-8 flex flex-col items-center gap-2 text-slate-500">
              <Loader2 className="w-6 h-6 animate-spin" />
              <div className="text-sm">Querying MySQL and computing diff…</div>
              <div className="text-xs text-slate-400">This may take 1-10 min depending on brand count.</div>
            </div>
          )}

          {error && (
            <div className="p-3 rounded-md bg-red-50 text-red-700 text-sm flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <div className="flex-1">{String(error)}</div>
              <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {preview && !loading && (
            <div className="space-y-4">
              {/* Top-line stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                <Stat label="Rows scanned" value={preview.rows_scanned} />
                <Stat label="Rows to change" value={preview.rows_changed}
                      accent={preview.rows_changed > 0 ? "fuchsia" : "slate"} />
                <Stat label="Customers affected" value={preview.unique_customers_affected} />
                <Stat label="Elapsed" value={`${preview.elapsed_secs || 0}s`} />
              </div>

              {/* Transition summary */}
              <div>
                <div className="text-xs font-medium text-slate-600 mb-1.5">Transitions</div>
                {summaryChips()}
              </div>

              {/* Safety-valve banner — appears whenever any credit-strip is queued */}
              {(preview.transitions?.held_for_review || 0) > 0 && (
                <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-sm flex items-start gap-2"
                     data-testid="reenrich-safety-banner">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
                  <div className="flex-1">
                    <div className="font-medium">
                      {preview.transitions.held_for_review.toLocaleString()} credit-strip transition{preview.transitions.held_for_review === 1 ? "" : "s"} require manual review.
                    </div>
                    <div className="mt-1 text-[13px] leading-snug">
                      Automation will apply only the {(preview.rows_changed - preview.transitions.held_for_review).toLocaleString()} safe gain{(preview.rows_changed - preview.transitions.held_for_review) === 1 ? "" : "s"} by default.
                      Any change that removes credit (yes/sms/unsure → no) is held back until you audit it — click <b>&quot;Apply all + reviewed strips&quot;</b> below only after spot-checking sample rows in MySQL to confirm the credits are being removed for the right reason.
                    </div>
                  </div>
                </div>
              )}

              {/* Samples per transition — collapsed by default */}
              {preview.samples && Object.keys(preview.samples).length > 0 && (
                <details className="text-sm" data-testid="reenrich-preview-samples">
                  <summary className="cursor-pointer text-slate-600 hover:text-slate-900 py-1">
                    Sample rows per transition ({Object.keys(preview.samples).length} kinds)
                  </summary>
                  <div className="mt-2 space-y-3">
                    {Object.entries(preview.samples).map(([kind, rows]) => {
                      const meta = TRANSITION_META[kind] || { label: kind, tone: "slate" };
                      return (
                        <div key={kind} className={`border rounded-md p-2 ${TONE_CLASSES[meta.tone]}`}>
                          <div className="text-xs font-semibold mb-1">{meta.label} — {rows.length} sample{rows.length === 1 ? "" : "s"}</div>
                          <div className="text-[11px] space-y-0.5 font-mono">
                            {rows.map((r, i) => (
                              <div key={i} className="flex flex-wrap gap-x-2">
                                <span className="text-slate-600">{r.customer_name || "—"}</span>
                                <span className="text-slate-400">brand {r.brand_id}</span>
                                <span className="text-slate-400">call {r.attempt_date_ist}</span>
                                <span>
                                  {r.prev_status || "no"}→<b>{r.new_status || "no"}</b>
                                </span>
                                {r.new_credit_date && r.new_credit_date !== r.prev_credit_date && (
                                  <span className="text-slate-500">credit-date {r.prev_credit_date || "—"}→<b>{r.new_credit_date}</b></span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </details>
              )}

              {/* Apply / Applied / Rolled-back states */}
              {applyState.status === "applied" && (
                <div className="p-3 rounded-md bg-emerald-50 text-emerald-800 text-sm flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  <div className="flex-1">
                    Applied <b>{applyState.applied.toLocaleString()}</b> row updates.
                    You have one-click undo available below.
                  </div>
                </div>
              )}
              {applyState.status === "rolled_back" && (
                <div className="p-3 rounded-md bg-sky-50 text-sky-800 text-sm flex items-center gap-2">
                  <Undo2 className="w-4 h-4" />
                  <div className="flex-1">Rolled back. Rows restored to their pre-apply state.</div>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2 flex-wrap">
            {applyState.status === "applied" && applyState.rollback_id && (
              <Button
                variant="outline"
                onClick={runRollback}
                data-testid="reenrich-rollback-btn"
                className="border-red-300 text-red-700 hover:bg-red-50"
              >
                <Undo2 className="w-4 h-4 mr-1.5" /> Undo last apply
              </Button>
            )}
            {applyState.status === "rolling_back" && (
              <Button variant="outline" disabled>
                <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> Rolling back…
              </Button>
            )}
            <div className="flex-1" />
            <Button variant="outline" onClick={() => setOpen(false)}
              data-testid="reenrich-preview-close">
              Close
            </Button>
            {preview && preview.rows_changed > 0 && applyState.status === "idle" && (() => {
              const held = preview.transitions?.held_for_review || 0;
              const safeCount = preview.rows_changed - held;
              return (
                <>
                  <Button onClick={() => runApply(false)}
                    data-testid="reenrich-apply-safe-btn"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white">
                    Apply safe {safeCount.toLocaleString()} gain{safeCount === 1 ? "" : "s"}
                  </Button>
                  {held > 0 && (
                    <Button
                      onClick={() => {
                        if (window.confirm(
                          `You are about to REMOVE credit from ${held.toLocaleString()} row(s). ` +
                          `Have you manually verified these are legitimate credit removals? This action is reversible via 'Undo last apply' but should only be run once you've spot-checked.`
                        )) {
                          runApply(true);
                        }
                      }}
                      data-testid="reenrich-apply-with-strips-btn"
                      variant="outline"
                      className="border-amber-400 text-amber-800 hover:bg-amber-50"
                      title="Includes credit-strip transitions — manual review required"
                    >
                      Apply all + {held.toLocaleString()} reviewed strip{held === 1 ? "" : "s"}
                    </Button>
                  )}
                </>
              );
            })()}
            {applyState.status === "applying" && (
              <Button disabled className="bg-fuchsia-600 text-white">
                <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> Applying…
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Stat({ label, value, accent = "slate" }) {
  const tones = {
    fuchsia: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200",
    slate:   "bg-slate-50 text-slate-700 border-slate-200",
  };
  return (
    <div className={`rounded-md border px-3 py-2 ${tones[accent] || tones.slate}`}>
      <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-lg font-semibold">
        {typeof value === "number" ? value.toLocaleString() : value}
      </div>
    </div>
  );
}

// Placeholder to silence a linter false-positive on the unused-var check
// for the numeric filter above; kept minimal.
function _norm_k(x) { return x; }
